import { onBeforeUnmount, shallowRef } from 'vue'

const DEFAULT_LOAD_MARGIN = '60% 0px'

/**
 * Tracks which cards are within the video load window so their real
 * `source_url` can be bound onto `<video src>` only near the viewport,
 * instead of every card fetching video bytes on mount. Each card is judged
 * independently, so this fits layouts where several cards can be on screen
 * at once. Used only by `ShowcaseVideoList.vue`'s `eagerPreload` mode
 * (the `/dramas` browsing grid, where preloading ahead of hover is a
 * deliberate choice) — other grid consumers load on hover/focus instead,
 * since there's no browsing-ahead intent to justify fetching before that.
 */
export function useVideoLoadWindow({ loadMargin = DEFAULT_LOAD_MARGIN } = {}) {
  const loadedVideoIds = shallowRef(new Set())
  let observer = null

  function observeCards(root, cards) {
    if (typeof IntersectionObserver !== 'function') {
      loadedVideoIds.value = new Set(cards.map(cardVideoId))
      return
    }
    if (!observer) observer = new IntersectionObserver(handleIntersect, { root, rootMargin: loadMargin, threshold: 0 })
    for (const card of cards) observer.observe(card)
  }

  function handleIntersect(entries) {
    const next = new Set(loadedVideoIds.value)
    for (const entry of entries) {
      const videoId = cardVideoId(entry.target)
      if (entry.isIntersecting) next.add(videoId)
      else next.delete(videoId)
    }
    loadedVideoIds.value = next
  }

  function isVideoLoaded(videoId) {
    return loadedVideoIds.value.has(videoId)
  }

  onBeforeUnmount(() => observer?.disconnect())

  return { observeCards, isVideoLoaded }
}

function cardVideoId(card) {
  const videoId = card.dataset.videoId
  if (!videoId) throw new Error('Video load window card is missing data-video-id')
  return videoId
}
